// "Goes with": parts that are always installed together with this one (brake sensor + harness, DEF doser + gasket, manifold + 4 seals).
// When the part goes on a work order, these go on too — the line can still be changed or removed on that job.
import { useMemo, useRef, useState } from 'react';
import { api, useApi, type Row } from '../lib';
import { PartSuggest, rankParts, useSuggestKeys } from '../partsuggest';
import { Icon, useToast } from '../ui';

export function PartLinks({ partId }: { partId: string }) {
  const toast = useToast();
  const data = useApi<Row>(`/api/parts/${partId}/links`, [partId]);
  const catalog = useApi<Row[]>('/api/parts');
  const [q, setQ] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  const links = (data.data?.links || []) as Row[];
  const usedBy = (data.data?.used_by || []) as Row[];
  const { items, total } = useMemo(() => rankParts((catalog.data || []).filter(p => p.id !== partId && !links.some(l => l.child_id === p.id)), q), [catalog.data, q, links, partId]);
  const keys = useSuggestKeys(items.length, q.trim().length >= 2);
  const save = async (next: { child_id: string; qty: number }[]) => {
    try { await api(`/api/parts/${partId}/links`, { method: 'PUT', body: { links: next } }); data.reload(); }
    catch (e) { toast((e as Error).message, 'err'); }
  };
  const list = links.map(l => ({ child_id: l.child_id as string, qty: Number(l.qty) || 1 }));
  const add = (p: Row) => { setQ(''); void save([...list, { child_id: p.id, qty: 1 }]).then(() => toast(`${p.name} goes with this part now`)); };
  return (
    <div className="part-links full">
      <b>🔗 Goes with — always installed together</b>
      <small className="muted block">When this part goes on a job, these go on too (the mechanic doesn't forget the gasket). Not needed that time? Change the qty or remove it on the job.</small>
      {links.length > 0 && <ul className="pl-list">{links.map(l => (
        <li key={l.child_id}><span className="grow"><b>{l.name}</b><small className="muted"> {l.sku}{l.track_qty !== 0 ? ` · ${l.stock} on the shelf` : ''}</small></span>
          <label className="pl-qty">×<input inputMode="numeric" defaultValue={l.qty} aria-label={`How many ${l.name}`}
            onBlur={e => { const n = Math.max(1, Math.min(100, Math.round(Number(e.target.value) || 1))); if (n !== l.qty) void save(list.map(x => x.child_id === l.child_id ? { ...x, qty: n } : x)); }} /></label>
          <button type="button" className="icon-btn" title="Not linked any more" onClick={() => void save(list.filter(x => x.child_id !== l.child_id))}><Icon name="x" size={14} /></button></li>))}</ul>}
      <div className="pl-add">
        <input ref={ref} value={q} onChange={e => setQ(e.target.value)} placeholder="+ Add a part that goes with it (gasket, harness, seal…)" autoComplete="off"
          onKeyDown={e => { const i = keys.onKey(e); if (i !== null && items[i]) add(items[i]); }} />
        <PartSuggest anchor={ref} open={q.trim().length >= 2} q={q} items={items} total={total} hi={keys.hi} onHover={keys.setHi} showPrice={false} onPick={add} />
      </div>
      {usedBy.length > 0 && <small className="muted block">This part goes with: {usedBy.map(u => `${u.name} (${u.sku})${Number(u.qty) > 1 ? ` ×${u.qty}` : ''}`).join(', ')}</small>}
    </div>
  );
}
